import {
  ComboboxPopover,
  type Option,
} from '@/components/ui/combobox/combobox-popover';
import type { Deal } from '@/models/registry';
import type { Payment } from '@/models/types';
import {
  AppWindow,
  AppWindowMac,
  Banknote,
  CreditCard,
  Dock,
  HandCoins,
  Landmark,
  Signature,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { PaymentMethodBadge } from './payment-method-badge';

// wire_transfer, personal_check, cash, credit_card, debit_card, cashiers_check, ach_plaid, ach_other
export const PAYMENT_METHOD_OPTIONS_MAP: Option<Payment['method']>[] = [
  {
    label: 'Wire Transfer',
    value: 'wire_transfer',
    icon: <Landmark />,
    hotkey: '1',
    abbreviation: 'wt',
  },
  {
    label: 'Personal Check',
    value: 'personal_check',
    icon: <Signature />,
    hotkey: '2',
    abbreviation: 'pch',
  },
  {
    label: 'Cash',
    value: 'cash',
    icon: <HandCoins />,
    hotkey: '3',
    abbreviation: 'cash',
  },
  {
    label: 'Credit Card',
    value: 'credit_card',
    icon: <Dock />,
    hotkey: '4',
    abbreviation: 'cc',
  },
  {
    label: 'Debit Card',
    value: 'debit_card',
    icon: <CreditCard />,
    hotkey: '5',
    abbreviation: 'dc',
  },
  {
    label: 'Cashiers Check',
    value: 'cashiers_check',
    icon: <Banknote />,
    hotkey: '6',
    abbreviation: 'cch',
  },
  {
    label: 'ACH Plaid',
    value: 'ach_plaid',
    icon: <AppWindow />,
    hotkey: '7',
    abbreviation: 'achp',
  },
  {
    label: 'ACH Other',
    value: 'ach_other',
    icon: <AppWindowMac />,
    hotkey: '8',
    abbreviation: 'acho',
  },
];

type PaymentMethodSelectorCombobox = {
  value: Payment['method'];
  setValue: (value: Payment['method']) => void;
};

export function PaymentMethodSelectorCombobox({
  value,
  setValue,
}: PaymentMethodSelectorCombobox) {
  const [open, setOpen] = useState(false);
  const handlePaymentMethodChange = (
    newPaymentMethod: Deal['payments'][0]['method'],
  ) => {
    setValue(newPaymentMethod);
  };

  const currentOption = useMemo(() => {
    return PAYMENT_METHOD_OPTIONS_MAP.find((option) => option.value === value);
  }, [value]);

  return (
    <ComboboxPopover
      open={open}
      onOpenChange={setOpen}
      endAdornment={null}
      placeholder="Set payment method..."
      onChange={handlePaymentMethodChange}
      emptyMessage="No payment method found"
      currentOption={currentOption}
      options={PAYMENT_METHOD_OPTIONS_MAP}
      // side={side}
      align="start"
      onKeyDown={(e) => {
        const keyPressed = e.key;

        const option = PAYMENT_METHOD_OPTIONS_MAP.find(
          (option) => option.hotkey === keyPressed,
        );

        if (option) {
          handlePaymentMethodChange(option.value);
          e.preventDefault();
          e.stopPropagation();
          setOpen(false);
        }
      }}
      stayOpenAfterSelection={false}
    >
      <div
      // DEV NOTE: without this div, combobox-popover won't work
      // that is, clicks will not open the combobox
      // I think the reason is children node needs to be able to receive a ref
      // in order to detect a click event, which makes it open
      // thus so this div is a workaround
      >
        <PaymentMethodBadge value={value} />
      </div>
    </ComboboxPopover>
  );
}
